import { useAppDispatch, useAppSelector } from "@/shared/lib/redux"
import { Table } from "@/shared/ui/table"
import { selectCurrentPage, selectPartnerTransactions, selectPartnerTransactionsLoading, selectTotalPages, setPage } from "../../model"
import { ColumnDef } from "@tanstack/react-table"
import { IPartnerTransaction } from "@/shared/api/partners"
import { NoTableData } from "@/shared/ui/no-table-data"
import { Loader } from "@/shared/ui/loader"
import { Pagination } from "@/shared/ui/pagination"
import { ActionButton } from "@/shared/ui/action-button"
import { Link, useNavigate } from "react-router-dom"
import ArrowLeftSvg from '@/shared/assets/images/ArrowBendDownRight.svg'
import { toLocaleDateTimeString } from "@/shared/lib/date-formatters"

export const PartnerTransactionsTable = () => {
    const transactions = useAppSelector(selectPartnerTransactions)
    const page = useAppSelector(selectCurrentPage)
    const totalPages = useAppSelector(selectTotalPages)
    const isLoading = useAppSelector(selectPartnerTransactionsLoading)
    const dispatch = useAppDispatch()
    const navigate = useNavigate()

    const columns: ColumnDef<IPartnerTransaction, any>[] = [
        {
            accessorFn: (transaction) => (
                <div className="w-full flex justify-end gap-2">
                    <ActionButton
                        onClick={() => navigate(`/partner/transactions/${transaction.id}`)}
                        icon={ArrowLeftSvg}
                    />
                </div>
            ),
            id: 'actions',
            enableSorting: false,
            cell: info => info.getValue(),
            header: () => <div/>,
        },
        {
            accessorFn: (transaction) => (
                <div>
                    {transaction.rrn ?? "—"}
                </div>
            ),
            id: 'rrn',
            cell: info => info.getValue(),
            enableSorting: false,
            header: () => <div className="!text-start text-nowrap">RRN</div>,
        },
        {
            accessorFn: (transaction) => (
                <div>
                    {transaction.cardNumber}
                </div>
            ),
            id: 'cardNumber',
            cell: info => info.getValue(),
            enableSorting: false,
            header: () => <div className="!text-start text-nowrap">Номер карты</div>,
        },
        {
            accessorFn: (transaction) => (
                <div>
                    {transaction.clientType === 'legal'
                        ? transaction.clientName ?? "—"
                        : transaction.clientName
                            ? `${transaction.clientName}`
                            : "—"
                    }
                </div>
            ),
            id: 'clientName',
            cell: info => info.getValue(),
            enableSorting: false,
            header: () => <div className="!text-start text-nowrap">Покупатель</div>,
        },
        {
            accessorFn: (transaction) => (
                <div>
                    {transaction.clientType === "legal"
                        ? transaction.clientInfo ?? "—"
                        : transaction.clientInfo
                            ? <Link className="hover:cursor-pointer hover:underline text-color-primary font-semibold" to={`/users/profile/${transaction.clientId}`}>{transaction.clientInfo}</Link>
                            : "—"
                    }
                </div>
            ),
            id: 'clientInfo',
            cell: info => info.getValue(),
            enableSorting: false,
            header: () => <div className="!text-start text-nowrap">ИНН / ID Покупателя</div>,
        },
        {
            accessorFn: (transaction) => (
                <div>
                    {transaction.storeName ?? "—"}
                </div>
            ),
            id: 'storeName',
            cell: info => info.getValue(),
            enableSorting: false,
            header: () => <div className="!text-start text-nowrap">Торговая точка</div>,
        },
        {
            accessorFn: (transaction) => (
                <div>
                    {transaction.isReturn
                        ? 'Возврат'
                        : 'Покупка'
                    }
                </div>
            ),
            id: 'isReturn',
            enableSorting: false,
            cell: info => info.getValue(),
            header: () => <div className="!text-start text-nowrap">Тип операции</div>,
        },
        {
            accessorFn: (transaction) => (
                <div>
                    {transaction.total}
                </div>
            ),
            enableSorting: false,
            id: 'total',
            cell: info => info.getValue(),
            header: () => <div className="!text-start text-nowrap">Сумма транзакции, руб.</div>,
        },
        {
            accessorFn: (transaction) => (
                <div>
                    {transaction.discountTotal}
                </div>
            ),
            id: 'discount',
            enableSorting: false,
            cell: info => info.getValue(),
            header: () => <div className="!text-start text-nowrap">скидка, руб.</div>,
        },
        {
            accessorFn: (transaction) => (
                <div>
                    {transaction.bonusTotal}
                </div>
            ),
            id: 'bonus',
            cell: info => info.getValue(),
            enableSorting: false,
            header: () => <div className="!text-start text-nowrap">бонусная скидка, руб.</div>,
        },
        {
            accessorFn: (transaction) => (
                <div>
                    {transaction.taxTotal}
                </div>
            ),
            id: 'tax',
            enableSorting: false,
            cell: info => info.getValue(),
            header: () => <div className="!text-start text-nowrap">НДС, руб.</div>,
        },
        {
            accessorFn: (transaction) => (
                <div className="text-nowrap">
                    {toLocaleDateTimeString(transaction.timeStamp)}
                </div>
            ),
            id: 'date',
            enableSorting: false,
            cell: info => info.getValue(),
            header: () => <div className="!text-start text-nowrap">Дата операции</div>,
        },
    ]

    return (
        <>
            {Boolean(transactions.length) &&
                <div className="table_box">
                    <Table columns={columns} data={transactions} />
                    <Pagination 
                        page={page} 
                        totalPages={totalPages} 
                        setPage={(page) => dispatch(setPage(page))
                    }                    
                    />
                </div>
            }
            {(!Boolean(transactions.length) && isLoading) &&
                <div className="info_panel">
                    <Loader />
                </div>
            }
            {(!Boolean(transactions.length) && !isLoading) &&
                <NoTableData label="Нет данных о транзакциях"/>
            }
        </>
    )
}